import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Link } from "wouter";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { useErrorDialog } from "@/hooks/use-error-dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertTriangle, CheckCircle2, XCircle, Loader2, ExternalLink, ShieldAlert } from "lucide-react";

interface OpenEscalation {
  eventId: string;
  actionItemId: string;
  actionItemTitle: string;
  requestedBy: string | null;
  escalatedTo: string | null;
  displacedHolder: string | null;
  newHolder: string | null;
  note: string | null;
  createdAt: string;
}

function formatDateTime(s: string): string {
  try {
    return new Date(s).toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return s;
  }
}

export default function EscalationsPage() {
  const { canConfirmDroppedTheBall, teamMemberName } = useAuth();
  const { toast } = useToast();
  const { showFromApiError } = useErrorDialog();
  const [showAll, setShowAll] = useState(false);

  const { data, isLoading } = useQuery<{ data: OpenEscalation[] }>({
    queryKey: ["/api/escalations", { all: showAll }],
    queryFn: async () => {
      const url = showAll ? "/api/escalations?all=true" : "/api/escalations";
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
  });

  const items = data?.data || [];

  return (
    <div className="max-w-5xl mx-auto px-6 py-6 space-y-4" data-testid="page-escalations">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-white flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-[#FF9F43]" />
            Stalled-Task Escalations
          </h1>
          <p className="text-xs text-white/50 mt-1 max-w-2xl">
            Items below were escalated to you because you are the senior reviewer for the holder
            who was suspected of dropping the ball. Give a justification, then
            <strong className="text-white/70"> Approve</strong> (records a KPI hit on the displaced holder)
            or <strong className="text-white/70">Reject</strong> (no KPI impact). Either way you may keep
            the item yourself or reassign it to someone in your own team. The displaced holder is not
            notified by email — talk to them directly. The requester is emailed the outcome.
          </p>
        </div>
        {canConfirmDroppedTheBall && (
          <div className="flex items-center gap-2 text-xs">
            <Button
              variant={showAll ? "ghost" : "default"}
              size="sm"
              onClick={() => setShowAll(false)}
              data-testid="button-filter-mine"
            >
              Mine ({teamMemberName || "—"})
            </Button>
            <Button
              variant={showAll ? "default" : "ghost"}
              size="sm"
              onClick={() => setShowAll(true)}
              data-testid="button-filter-all"
            >
              All open
            </Button>
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 text-white/60 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading escalations…
        </div>
      ) : items.length === 0 ? (
        <Card className="border-white/10 bg-white/5">
          <CardContent className="p-8 text-center text-white/50 text-sm" data-testid="text-no-escalations">
            No open escalations.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((e) => (
            <EscalationRow key={e.eventId} esc={e} onSuccess={(msg) => {
              toast({ title: msg });
              queryClient.invalidateQueries({ queryKey: ["/api/escalations"] });
            }} onError={(err) => showFromApiError("Resolve failed", err)} />
          ))}
        </div>
      )}
    </div>
  );
}

function EscalationRow({ esc, onSuccess, onError }: {
  esc: OpenEscalation;
  onSuccess: (msg: string) => void;
  onError: (err: Error) => void;
}) {
  const [justification, setJustification] = useState("");
  const [disposition, setDisposition] = useState<"leave" | "reassign">("leave");
  const [reassignTo, setReassignTo] = useState("");
  const [pending, setPending] = useState<"APPROVE" | "REJECT" | null>(null);

  // Who the reviewer may reassign to — their own team. Only fetched once the
  // reviewer chooses to reassign, to keep the queue lightweight.
  const { data: optionsResp, isLoading: optionsLoading } = useQuery<{ data: string[] }>({
    queryKey: ["/api/escalations/reassign-options"],
    enabled: disposition === "reassign",
  });
  const reassignOptions = optionsResp?.data || [];

  const justificationValid = justification.trim().length > 0;
  const reassignValid = disposition === "leave" || !!reassignTo;
  const canSubmit = justificationValid && reassignValid;

  const resolve = useMutation({
    mutationFn: async (outcome: "APPROVE" | "REJECT") => {
      const res = await apiRequest("POST", `/api/escalations/${esc.eventId}/resolve`, {
        outcome,
        justification: justification.trim(),
        disposition,
        reassignTo: disposition === "reassign" ? reassignTo : undefined,
      });
      return res.json();
    },
    onSuccess: (_data, outcome) => {
      setPending(null);
      const where = disposition === "reassign"
        ? `reassigned to ${reassignTo}`
        : "kept with you";
      onSuccess(outcome === "APPROVE"
        ? `Approved — KPI hit recorded against ${esc.displacedHolder || "the displaced holder"}; item ${where}.`
        : `Rejected — no KPI impact; item ${where}.`);
    },
    onError: (err: Error) => { setPending(null); onError(err); },
  });

  const submit = (outcome: "APPROVE" | "REJECT") => {
    if (!canSubmit) return;
    setPending(outcome);
    resolve.mutate(outcome);
  };

  return (
    <Card className="border-white/10 bg-white/5" data-testid={`card-escalation-${esc.eventId}`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <Link
              href={`/action-items/${esc.actionItemId}`}
              className="text-sm font-semibold text-white hover:text-cyan-300 inline-flex items-center gap-1.5"
              data-testid={`link-item-${esc.actionItemId}`}
            >
              {esc.actionItemTitle}
              <ExternalLink className="h-3 w-3 opacity-60" />
            </Link>
            <div className="text-sm text-white/40 mt-0.5">
              Raised {formatDateTime(esc.createdAt)} by <span className="text-white/70">{esc.requestedBy || "—"}</span>
            </div>
          </div>
          <Badge variant="outline" className="border-[#FF9F43]/40 text-[#FF9F43] text-xs">
            <AlertTriangle className="h-3 w-3 mr-1" /> Pending review
          </Badge>
        </div>

        <div className="grid grid-cols-2 gap-3 text-xs">
          <div>
            <div className="text-white/40 uppercase text-xs font-semibold">Displaced holder</div>
            <div className="text-white">{esc.displacedHolder || "—"}</div>
          </div>
          <div>
            <div className="text-white/40 uppercase text-xs font-semibold">Now with</div>
            <div className="text-white">{esc.newHolder || "—"}</div>
          </div>
        </div>

        {esc.note && (
          <div className="bg-white/5 border border-white/10 rounded p-3 text-xs text-white/80 whitespace-pre-wrap">
            <div className="text-white/40 uppercase text-xs font-semibold mb-1">Reason from {esc.requestedBy || "raiser"}</div>
            {esc.note}
          </div>
        )}

        <div className="space-y-1.5">
          <Label className="text-xs text-white/60">
            Justification <span className="text-[#FF9F43]">(required)</span>
          </Label>
          <Textarea
            value={justification}
            onChange={(e) => setJustification(e.target.value)}
            placeholder="Explain your decision — recorded on the audit log and shown to the requester."
            className="bg-white/5 border-white/10 text-white text-sm min-h-[60px]"
            data-testid={`textarea-justification-${esc.eventId}`}
          />
        </div>

        <div className="space-y-2">
          <Label className="text-xs text-white/60">What happens to the item?</Label>
          <RadioGroup
            value={disposition}
            onValueChange={(v) => setDisposition(v as "leave" | "reassign")}
            className="space-y-1.5"
          >
            <Label
              htmlFor={`disp-leave-${esc.eventId}`}
              className="flex items-center gap-2.5 text-sm text-white/90 cursor-pointer"
            >
              <RadioGroupItem value="leave" id={`disp-leave-${esc.eventId}`} data-testid={`radio-leave-${esc.eventId}`} />
              Keep it with me ({esc.newHolder || "the reviewer"})
            </Label>
            <Label
              htmlFor={`disp-reassign-${esc.eventId}`}
              className="flex items-center gap-2.5 text-sm text-white/90 cursor-pointer"
            >
              <RadioGroupItem value="reassign" id={`disp-reassign-${esc.eventId}`} data-testid={`radio-reassign-${esc.eventId}`} />
              Reassign within my team
            </Label>
          </RadioGroup>

          {disposition === "reassign" && (
            <Select value={reassignTo} onValueChange={setReassignTo}>
              <SelectTrigger
                className="bg-white/5 border-white/10 text-white text-sm"
                data-testid={`select-reassign-to-${esc.eventId}`}
              >
                <SelectValue placeholder={optionsLoading ? "Loading your team…" : "Pick a colleague in your team"} />
              </SelectTrigger>
              <SelectContent className="bg-[#0f1d40] border-white/10 text-white">
                {reassignOptions.length === 0 && !optionsLoading ? (
                  <div className="px-2 py-1.5 text-xs text-white/40">No colleagues found in your team.</div>
                ) : (
                  reassignOptions.map((name) => (
                    <SelectItem key={name} value={name} data-testid={`option-reassign-${name}`}>
                      {name}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          )}
        </div>

        <div className="flex items-center justify-end gap-2">
          <Button
            variant="ghost"
            size="sm"
            disabled={resolve.isPending || !canSubmit}
            onClick={() => submit("REJECT")}
            data-testid={`button-reject-${esc.eventId}`}
          >
            {pending === "REJECT" && resolve.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
            ) : (
              <XCircle className="h-3.5 w-3.5 mr-1.5" />
            )}
            Reject (no KPI hit)
          </Button>
          <Button
            size="sm"
            disabled={resolve.isPending || !canSubmit}
            className="bg-[#EA5455] hover:bg-[#EA5455]/90 text-white"
            onClick={() => submit("APPROVE")}
            data-testid={`button-approve-${esc.eventId}`}
          >
            {pending === "APPROVE" && resolve.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
            ) : (
              <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
            )}
            Approve (KPI hit)
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
